import { useEffect, useRef } from "react";
import { useExclusiveAction } from '../../hooks/useExclusiveAction';
import { useRequestScope } from '../../hooks/useRequestScope';
import { useScopedState } from '../../hooks/useScopedState';
import { useAppBootstrap } from "../../providers/AppBootstrapProvider";
import { authService, formatAuthErrorMessage } from "../../services/authService";

const defaultServices = { get authService() { return authService; } };

export function useEmailVerificationViewModel({ requiredForRegistration = false }: { requiredForRegistration?: boolean }, services = defaultServices) {
  const { authService } = services;

  const { currentUser, claims, refreshClaims } = useAppBootstrap();
  const scopeKey = JSON.stringify([currentUser?.uid]);
  const requestScope = useRequestScope(scopeKey, services);
  const [busy, setBusy] = useScopedState(scopeKey, false);
  const [message, setMessage] = useScopedState(scopeKey, "");
  const [failed, setFailed] = useScopedState(scopeKey, false);
  const [verified, setVerified] = useScopedState(scopeKey, Boolean(currentUser?.emailVerified && (!requiredForRegistration || claims?.email_verified === true)));
  useEffect(() => {
    if (requiredForRegistration) setVerified(Boolean(currentUser?.emailVerified && claims?.email_verified === true));
  }, [requiredForRegistration, currentUser?.emailVerified, claims?.email_verified, setVerified]);
  const activeRequest = useRef(false);
  const uid = currentUser?.uid;
  const initialNotice = uid ? authService.getVerificationNotice(uid) : undefined;
  const run = useExclusiveAction(requestScope, async (action: "send" | "refresh") => {
    if (activeRequest.current || !uid) return;
    const isCurrentOperation = requestScope.capture();
    activeRequest.current = true;
    setBusy(true);
    setMessage("");
    setFailed(false);
    try {
      if (action === "send") {
        await authService.sendCurrentUserVerification();
        if (!isCurrentOperation()) return;
        if (authService.getCurrentUser()?.uid === uid) setMessage("Verification email sent. Check your inbox and spam folder.");
      } else {
        const complete = await authService.refreshEmailVerification();
        if (!isCurrentOperation()) return;
        if (authService.getCurrentUser()?.uid !== uid) return;
        if (complete || requiredForRegistration) await refreshClaims(false);
        if (authService.getCurrentUser()?.uid !== uid) return;
        if (complete) {
          setVerified(true);
        } else {
          setVerified(false);
          setMessage("Your email is not verified yet. Open the link in your verification email, then try again.");
        }
      }
    } catch (error) {
      if (!isCurrentOperation()) return;
      if (authService.getCurrentUser()?.uid !== uid) return;
      setFailed(true);
      setMessage(formatAuthErrorMessage(error, "emailVerification", "Could not verify your email. Please try again."));
    } finally {
      if (isCurrentOperation()) { activeRequest.current = false; setBusy(false); }
    }
  });
  const refreshRef = useRef(run);
  refreshRef.current = run;
  useEffect(() => {
    if (!requiredForRegistration || !uid) return;
    const refresh = () => {
      if (document.visibilityState === "visible") void refreshRef.current("refresh");
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [requiredForRegistration, uid]);
  useEffect(() => { activeRequest.current = false; }, [requestScope]);

  return { currentUser, busy, message, failed, verified, initialNotice, run };
}
